/**
 * Soft auth gate helper — call before any buy/checkout action.
 *
 * If storefront auth is required in "soft" mode and no session exists,
 * redirects the user to /auth-gate with intent=buy and preserves the
 * current path as returnTo. Returns `true` when the caller should
 * abort the buy action (i.e. we redirected), `false` when the caller
 * may proceed.
 *
 * Reads the persisted Supabase session from localStorage synchronously
 * so we don't need to await `getSession()` on every click.
 */
import { useStorefrontSettings } from "@/stores/storefrontSettingsStore";
import { hasGuestPass } from "@/lib/guestPass";

const SUPABASE_AUTH_KEY = `sb-${import.meta.env.VITE_SUPABASE_PROJECT_ID}-auth-token`;
const GATE_RETURN_KEY = "khays_auth_gate_return";

/** True when a persisted (non-expired) Supabase session exists in localStorage. */
export function hasPersistedSession(): boolean {
  try {
    const raw = localStorage.getItem(SUPABASE_AUTH_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    const expiresAt = parsed?.expires_at ?? parsed?.currentSession?.expires_at;
    if (typeof expiresAt === "number") {
      return expiresAt * 1000 > Date.now();
    }
    // Fallback: presence of an access_token is enough — Supabase will refresh.
    return !!(parsed?.access_token || parsed?.currentSession?.access_token);
  } catch {
    return false;
  }
}

/**
 * If soft gate is active and no session, redirect to /auth-gate.
 * @returns true if the caller should STOP (redirect in flight); false to continue.
 */
export function requireAuthForBuy(): boolean {
  const s = useStorefrontSettings.getState();
  const mode = s.storefrontAuthMode || "soft";
  // A guest pass downgrades the hard gate to the soft gate for this browser:
  // browsing is allowed, but buying still requires sign-in.
  const softActive =
    !!s.storefrontAuthRequired && (mode === "soft" || (mode === "hard" && hasGuestPass()));
  if (!softActive) return false;
  if (hasPersistedSession()) return false;
  try {
    sessionStorage.setItem(
      GATE_RETURN_KEY,
      `${window.location.pathname}${window.location.search}`,
    );
  } catch {}
  window.location.href = "/auth-gate?intent=buy";
  return true;
}
